import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { VoiceModuleHandler } from './voice-handler.interface';
import { VoiceIntent, VoiceExecutionResult } from '../models';
import { CalendarCrudService, OrganisationPresenceSummary } from '../../../modules/calendar/services/calendar-crud.service';
import { CalendarDataService } from '../../../modules/calendar/services/calendar-data.service';
import { AuthService } from '../../auth/auth.service';
import { CalendarPortion } from '../../../models/enums';
import { Calendar, CalendarLocalisation, CalendarPresence } from '../../../models/interfaces';
import { VoiceCalendarChoiceService } from '../services/voice-calendar-choice.service';
import { buildCalendarChoiceActions, pickLocalisationFor, readCalendarId } from './calendar-choice.util';
import {
  PresenceSummaryRow,
  formatPresenceSummary,
  ownMostFrequentPlace,
  pickHomePlace
} from './presence-summary.util';

import { isPresenceOverlapError } from '../../../modules/calendar/utils/presence-slots';
@Injectable({ providedIn: 'root' })
export class CalendarVoiceHandler implements VoiceModuleHandler {
  private calendarCrud = inject(CalendarCrudService);
  private calendarData = inject(CalendarDataService);
  private auth = inject(AuthService);
  private calendarChoice = inject(VoiceCalendarChoiceService);

  readonly supportedEndpoints = [
    'calendar.addPresences',
    'calendar.removePresences',
    'calendar.listPresences',
  ];

  async execute(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    switch (intent.endpoint) {
      case 'calendar.addPresences':
        return this.handleAddPresences(intent);
      case 'calendar.removePresences':
        return this.handleRemovePresences(intent);
      case 'calendar.listPresences':
        return this.handleListPresences(intent);
      default:
        return { success: false, message: `Action calendar non supportée: ${intent.endpoint}` };
    }
  }

  private async handleAddPresences(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const dates = intent.params['dates'] as string[];
    const portion = (intent.params['portion'] as CalendarPortion) || CalendarPortion.FullDay;

    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const resolved = await this.resolveCalendar(intent);
    if ('result' in resolved) return resolved.result;
    const calendar = resolved.calendar;

    let created = 0;
    const errors: string[] = [];

    const activeLocalisation = this.localisationFor(calendar);

    for (const date of dates) {
      try {
        const presenceData: Partial<CalendarPresence> = {
          date_presence: date,
          portion: portion,
          profile_id: profile.id,
          calendar_id: calendar.id,
          custom_name: profile.preferred_calendar_name
            || `${profile.first_name} ${profile.last_name}`,
          localisation_id: activeLocalisation?.id ?? null
        };

        const result = await firstValueFrom(
          this.calendarCrud.createPresence(presenceData)
        );

        if (result) {
          created++;
        }
      } catch (err) {
        // La base refuse un créneau qui chevauche une présence existante (MOKA-368).
        const msg = isPresenceOverlapError(err) ? 'créneau déjà pris'
          : err instanceof Error ? err.message : 'Erreur';
        errors.push(`${date}: ${msg}`);
      }
    }

    if (created > 0 && this.isOpenOnScreen(calendar)) {
      this.navigateToDate(dates[0]);
    }

    if (created === dates.length) {
      const dateStr = this.formatDate(dates[0]);
      const suffix = dates.length > 1 ? ` (${dates.length} dates)` : '';
      return {
        success: true,
        message: `${created} présence${created > 1 ? 's' : ''} ajoutée${created > 1 ? 's' : ''} pour ${dateStr}${suffix}`,
        data: { created, dates }
      };
    } else if (created > 0) {
      return {
        success: true,
        message: `${created}/${dates.length} présences ajoutées. Erreurs: ${errors.join(', ')}`,
        data: { created, errors }
      };
    } else {
      return {
        success: false,
        message: `Aucune présence ajoutée. Erreurs: ${errors.join(', ')}`
      };
    }
  }

  private async handleRemovePresences(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const dates = intent.params['dates'] as string[];

    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const resolved = await this.resolveCalendar(intent);
    if ('result' in resolved) return resolved.result;
    const calendar = resolved.calendar;

    const startDate = dates.reduce((min, d) => d < min ? d : min, dates[0]);
    const endDate = dates.reduce((max, d) => d > max ? d : max, dates[0]);

    const presences = await firstValueFrom(
      this.calendarCrud.getPresences({
        startDate,
        endDate,
        calendarId: calendar.id
      })
    );

    const toDelete = presences.filter(
      p => p.profile_id === profile.id && dates.includes(p.date_presence)
    );

    if (toDelete.length === 0) {
      return {
        success: false,
        message: 'Aucune présence trouvée pour ces dates'
      };
    }

    let deleted = 0;
    for (const presence of toDelete) {
      try {
        await firstValueFrom(
          this.calendarCrud.deletePresence(presence.id as number)
        );
        deleted++;
      } catch {
        // Ignorer les erreurs individuelles
      }
    }

    if (this.isOpenOnScreen(calendar)) {
      this.calendarData.getPresences(this.calendarData.selectedDate());
    }

    return {
      success: deleted > 0,
      message: deleted > 0
        ? `${deleted} présence${deleted > 1 ? 's' : ''} supprimée${deleted > 1 ? 's' : ''}`
        : 'Aucune présence supprimée',
      data: { deleted }
    };
  }

  /**
   * Calendrier visé par une action de présence (MOKA-407), dans l'ordre :
   * 1. `calendarId` posé par un bouton de choix (jamais par le modèle : il
   *    doit appartenir aux calendriers accessibles de la personne) ;
   * 2. le calendrier ouvert à l'écran ;
   * 3. le seul calendrier accessible, s'il n'y en a qu'un ;
   * 4. sinon Koffi demande lequel, en boutons (dernier utilisé en premier) :
   *    le clic rejoue la même action avec `calendarId`, sans reformuler.
   * « Rendez-vous sur la page Calendrier » ne reste que s'il n'existe aucun
   * calendrier accessible.
   */
  private async resolveCalendar(
    intent: VoiceIntent
  ): Promise<{ calendar: Calendar & { id: number } } | { result: VoiceExecutionResult }> {
    const accessible = this.calendarChoice.accessibleCalendars();
    const withId = (c: Calendar | null | undefined): (Calendar & { id: number }) | null =>
      c && typeof c.id === 'number' ? (c as Calendar & { id: number }) : null;

    const requested = readCalendarId(intent.params);
    const chosen = requested === null ? null : withId(accessible.find(c => c.id === requested));
    if (chosen) return { calendar: chosen };

    const active = withId(this.calendarData.calendarActif);
    if (active) return { calendar: active };

    if (accessible.length === 0) {
      return {
        result: {
          success: false,
          message: 'Aucun calendrier accessible. Rendez-vous sur la page Calendrier pour en configurer un.'
        }
      };
    }
    if (accessible.length === 1) return { calendar: accessible[0] as Calendar & { id: number } };

    const choices = await this.calendarChoice.choices();
    return {
      result: {
        success: false,
        awaitingChoice: true,
        message: 'Dans quel calendrier ?',
        suggestedActions: buildCalendarChoiceActions(intent.endpoint, intent.params, choices)
      }
    };
  }

  /** `true` si ce calendrier est celui affiché par la page Calendrier. */
  private isOpenOnScreen(calendar: Calendar): boolean {
    return this.calendarData.calendarActif?.id === calendar.id;
  }

  /**
   * Lieu de la présence : celui choisi à l'écran si ce calendrier est ouvert,
   * sinon la même règle que la page (lieu unique, préférence mémorisée, premier).
   */
  private localisationFor(calendar: Calendar): CalendarLocalisation | null {
    if (this.isOpenOnScreen(calendar)) {
      return this.calendarData.getActiveLocalisation() || this.calendarData.getUniqueLocalisation();
    }
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(`calendar_${calendar.id}_prefered_localisation_id`);
    } catch {
      // Stockage indisponible : on retombe sur le premier lieu.
    }
    return pickLocalisationFor(calendar.localisations ?? [], stored);
  }

  /**
   * MOKA-266 : « qui est au bureau demain ? » — regroupé par lieu, le lieu de
   * la personne en premier, noms bornés (formatage : `presence-summary.util.ts`).
   *
   * Un seul calendrier : une organisation peut en avoir plusieurs (présences,
   * absences, véhicules…) et « au bureau » ne mélange pas un congé avec une
   * présence. Calendrier retenu : celui ouvert à l'écran, sinon celui de la
   * dernière présence de la personne, sinon le seul de l'organisation, sinon
   * le plus rempli sur la période. Koffi répond donc aussi hors de la page
   * Calendrier (avant : « Aucun calendrier actif »).
   */
  private async handleListPresences(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const startDate = intent.params['startDate'] as string;
    const endDate = intent.params['endDate'] as string;
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const [presences, last] = await Promise.all([
      firstValueFrom(this.calendarCrud.getOrganisationPresencesSummary(startDate, endDate)),
      this.lastPresence(profile.id)
    ]);

    const calendarId = this.pickCalendarId(presences, last?.calendar_id ?? null);
    if (calendarId === null) {
      return { success: false, message: 'Aucun calendrier de présences dans votre organisation.' };
    }

    const rows = this.toSummaryRows(presences.filter(p => p.calendar_id === calendarId));
    const available = new Set(rows.map(r => r.placeKey));

    // Lieu de la personne, du plus sûr au moins sûr : là où elle a elle-même
    // posé sa présence sur la période ; le lieu choisi dans le calendrier
    // ouvert ; le lieu de sa dernière présence ; une agence du même nom.
    const homeKey = pickHomePlace([
      ownMostFrequentPlace(rows, profile.id),
      ...this.activePlaceKeys(),
      last?.localisation_id ? `loc:${last.localisation_id}` : null,
      this.agencyPlaceKey(rows)
    ], available);

    const message = formatPresenceSummary(rows, { startDate, endDate, today: this.todayIso(), homeKey });
    const people = new Set(rows.map(r => `${r.date}|${r.profileId}`)).size;

    return {
      success: true,
      message,
      data: { count: people, startDate, endDate, calendarId }
    };
  }

  /** Calendrier de la réponse (cf. `handleListPresences`) ; `null` si l'organisation n'en a aucun. */
  private pickCalendarId(presences: OrganisationPresenceSummary[], lastCalendarId: number | null): number | null {
    const orgCalendars = (this.auth.profile?.organisation?.calendars ?? [])
      .map(c => c.id)
      .filter((id): id is number => typeof id === 'number');
    const inOrg = (id: number | null | undefined): id is number => !!id && orgCalendars.includes(id);

    const active = this.calendarData.calendarActif?.id;
    if (inOrg(active)) return active;
    if (inOrg(lastCalendarId)) return lastCalendarId;
    if (orgCalendars.length === 1) return orgCalendars[0];

    const counts = new Map<number, number>();
    for (const p of presences) {
      if (p.calendar_id) counts.set(p.calendar_id, (counts.get(p.calendar_id) ?? 0) + 1);
    }
    const busiest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    return busiest ?? orgCalendars[0] ?? null;
  }

  /** Lignes du résumé : nom affiché et lieu (localisation, sinon « Sans lieu »). */
  private toSummaryRows(presences: OrganisationPresenceSummary[]): PresenceSummaryRow[] {
    return presences.map(p => {
      const fullName = `${p.profile?.first_name ?? ''} ${p.profile?.last_name ?? ''}`.trim();
      const label = p.localisation?.label?.trim();
      const located = !!p.localisation_id && !!label;
      return {
        date: p.date_presence,
        profileId: p.profile_id,
        name: p.custom_name?.trim() || fullName || 'Quelqu\'un',
        portion: p.portion,
        placeKey: located ? `loc:${p.localisation_id}` : 'none',
        placeLabel: located ? label! : 'Sans lieu'
      };
    });
  }

  private async lastPresence(profileId: string): Promise<{ calendar_id: number | null; localisation_id: number | null } | null> {
    try {
      return await firstValueFrom(this.calendarCrud.getLastPresenceForProfile(profileId));
    } catch {
      // Sans dernière présence : calendrier et lieu se déduisent autrement.
      return null;
    }
  }

  /** Lieu sélectionné dans le calendrier ouvert à l'écran (localisation active). */
  private activePlaceKeys(): string[] {
    const active = this.calendarData.getActiveLocalisation();
    return active?.id ? [`loc:${active.id}`] : [];
  }

  /** Lieu qui porte le nom d'une agence de la personne (« Paris » ↔ agence Paris). */
  private agencyPlaceKey(rows: PresenceSummaryRow[]): string | null {
    const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const agencies = new Set((this.auth.profile?.agencies ?? []).map(a => normalize(a.label ?? '')).filter(Boolean));
    return rows.find(r => agencies.has(normalize(r.placeLabel)))?.placeKey ?? null;
  }

  /** Date du jour `YYYY-MM-DD` dans le fuseau du navigateur. */
  private todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private navigateToDate(date: string): void {
    this.calendarData.selectedDate.set(date);

    const dateObj = new Date(date);
    this.calendarData.generateMonthMatrice(
      dateObj.getMonth(),
      dateObj.getFullYear()
    );

    this.calendarData.getPresences(date);
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }
}
